import { type FileUploadData, FilterPresetEnum, type InboxInvoice, type MatchingDataInterface, UploadKindEnum } from "@finway-group/shared/lib/models"
import { MAX_INVOICES_PER_EXPENSE } from "@finway-group/shared/lib/utils/validators/validationConsts"
import axios, { type ResponseType } from "axios"
import { i18n } from "~Shared/locales/i18n"
import { NotificationService, NotificationTypeEnum } from "~Shared/services/notification.service"
import { checkFilesSize } from "~Shared/utils/helper.utils"
import { openWindowInNewTab } from "~Shared/utils/window.utils"
import { convertUnencodedParamStringToObject } from "../config/axios.utils"
import { isFileExtensionAllowed } from "../utils/file.utils"

type SignedUrlData = {
    signedUrl: string
}

/**
 * Axios instance w/o base config and interceptors required for API
 */
const axios3rdParty = axios.create()
axios3rdParty.interceptors.request.clear()
axios3rdParty.interceptors.response.clear()

export const FileService = {
    upload: async (file: File, kind: UploadKindEnum, onProgress?: any) => {
        const { formData, config } = prepareFileUpload(file, onProgress)
        formData.append("kind", kind.toString())
        const { data } = await axios.post(`/files`, formData, config)
        return data as FileUploadData
    },

    uploadMultiple: async (files: Array<File>, kind: UploadKindEnum, onProgress?: any) => {
        const { formData, config } = prepareFilesUpload(files, onProgress)
        formData.append("kind", kind.toString())
        const { data } = await axios.post(`/files/multiple`, formData, config)
        return data as Array<FileUploadData>
    },

    uploadMatchingCSV: async (file: File, onProgress?: any) => {
        const { formData, config } = prepareFileUpload(file, onProgress)
        formData.append("kind", UploadKindEnum.CSV_MATCHING.toString())
        const { data } = await axios.post(`/matching/determine`, formData, config)
        return data as Array<MatchingDataInterface>
    },

    export: (filter: string, shouldSkipUpdate = false, subjectLines?: Record<string, string>) => {
        // Use "Follow up requests" preset because the API filters it out by default. By doing this we avoid our target expense to be filtered out.
        const params = convertUnencodedParamStringToObject(`${filter}&presets[in]=${FilterPresetEnum.FOLLOW_UP_REQUESTS}`)
        return axios.post(`export/expenses`, { subjectLines, shouldSkipUpdate }, { params })
    },

    getCSVFileData: async (url: string) => {
        const { data } = await axios.post(`/files/parse-csv`, { csvFileUrl: url })
        return data as Array<string>
    },

    getSignedUrl: (url: string) => axios.post<SignedUrlData>(`/files/signed-url`, { url }),

    downladSignedUrl: async <T = Blob>({ url, responseType = "blob" }: { url: string; responseType?: ResponseType }) => {
        const { data } = await FileService.getSignedUrl(url)
        return axios3rdParty.get<T>(data.signedUrl, { responseType })
    },

    exportExpenseSummary: async (id: string, language: string) => {
        const {
            data: { pdfUrl },
        } = await axios.post(`/export/expenses/${id}/summary`, { language })
        FileService.downloadFileAsSignedUrl(pdfUrl)
    },

    exportPurchaseOrderSummary: async (id: string, language: string) => {
        const {
            data: { pdfUrl },
        } = await axios.post(`/export/purchase-orders/${id}/summary`, { language })

        openWindowInNewTab({ url: pdfUrl, target: "_self" })
    },

    downloadFileAsSignedUrl: (fileUrl: string) => {
        FileService.getSignedUrl(fileUrl).then(({ data: { signedUrl } }) => openWindowInNewTab({ url: signedUrl, target: "_self" }))
    },
    checkFilesSizeAndHandleError: (files: Array<File>) => {
        if (!checkFilesSize(files)) {
            NotificationService.send(NotificationTypeEnum.ERROR, i18n.t("error:file.too_big.title"), i18n.t("error:file.too_big.message"))
            return false
        }
        return true
    },
    checkAllowedInvoicesAmountAndHandleError: (files: Array<File | InboxInvoice>, currentInvoicesCount: number) => {
        if (currentInvoicesCount + files.length > MAX_INVOICES_PER_EXPENSE) {
            NotificationService.send(NotificationTypeEnum.ERROR, i18n.t("error:file.too_many_invoices.title"), i18n.t("error:file.too_many_invoices.message"))
            return false
        }
        return true
    },
    checkFileSizeAndHandleError: (file: File) => FileService.checkFilesSizeAndHandleError([file]),
    checkIsFileExtensionValidAndShowNotification: (file: Pick<File, "name">, allowedFormats: ReadonlyArray<string>) => {
        const fileName = file.name
        if (isFileExtensionAllowed(fileName, allowedFormats)) {
            return true
        }
        NotificationService.send(
            NotificationTypeEnum.ERROR,
            i18n.t("error:file.unsupported_file_type.title"),
            i18n.t("error:file.unsupported_file_type.message", { allowedFormats: allowedFormats.join(", ") }),
        )
        return false
    },
}

export const prepareFileUpload = (file: File, onProgress?: any) => prepareFilesUpload([file], onProgress)

export const prepareFilesUpload = (files: Array<File>, onProgress?: any) => {
    const formData = new FormData()
    let config = {
        headers: { "Content-type": "multipart/form-data" },
        onUploadProgress: (_event: any) => {},
    }

    if (onProgress) {
        config = {
            ...config,
            onUploadProgress: (event: any) => {
                onProgress({ percent: (event.loaded / event.total) * 100 })
            },
        }
    }

    files.forEach((file) => formData.append("uploadFile", file))
    return { formData, config }
}
